import React, { useState } from 'react';
import { Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faCode,
  faInfoCircle,
  faListUl,
  faReply,
  faTerminal,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { Request } from '../types';
import {
  formatBytes,
  formatDate,
  methodVariant,
  retentionLabel,
  retentionVariant,
  statusVariant,
} from '../types';
import { generateCurlCommand } from '../utils/curl';
import CopyButton from './CopyButton';

interface RequestDetailProps {
  request: Request;
  slug: string;
  apiUrl: string;
  onExport: (request: Request) => void;
}

interface SectionProps {
  title: string;
  icon: IconDefinition;
  copyField: string;
  copyText: string;
  copyLabel: string;
  copiedField: string | null;
  onCopy: (text: string, field: string) => void;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({
  title,
  icon,
  copyField,
  copyText,
  copyLabel,
  copiedField,
  onCopy,
  children,
}) => (
  <section className="detail-section">
    <header className="detail-section__head">
      <h4 className="detail-section__title">
        <FontAwesomeIcon icon={icon} aria-hidden="true" className="detail-section__icon" />
        {title}
      </h4>
      <CopyButton
        field={copyField}
        text={copyText}
        label={copyLabel}
        copiedField={copiedField}
        onCopy={onCopy}
      />
    </header>
    <div className="detail-section__body">{children}</div>
  </section>
);

interface DefItemProps {
  label: string;
  field?: string;
  text?: string;
  copiedField?: string | null;
  onCopy?: (text: string, field: string) => void;
  children: React.ReactNode;
}

const DefItem: React.FC<DefItemProps> = ({ label, field, text, copiedField, onCopy, children }) => (
  <>
    <dt>{label}</dt>
    <dd>
      <span className="detail-list__value">{children}</span>
      {field && text !== undefined && onCopy ? (
        <CopyButton
          field={field}
          text={text}
          label={`Copy ${label}`}
          copiedField={copiedField ?? null}
          onCopy={onCopy}
        />
      ) : null}
    </dd>
  </>
);

const RequestDetail: React.FC<RequestDetailProps> = ({ request, slug, apiUrl, onExport }) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  // Raw first: what the sender actually sent is the answer the inspector exists
  // to give, and it is the only view a body that never parsed can offer.
  const [bodyView, setBodyView] = useState<'raw' | 'parsed'>('raw');

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField((current) => (current === field ? null : current)), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const headersText = JSON.stringify(request.headers || {}, null, 2);
  const queryParams = request.queryParams || {};
  const queryCount = Object.keys(queryParams).length;
  const queryText = JSON.stringify(queryParams, null, 2);
  const rawBody = typeof request.bodyRaw === 'string' ? request.bodyRaw : '';
  const bodyIsObject = request.body !== null && typeof request.body === 'object';
  const parsedBodyText = bodyIsObject ? JSON.stringify(request.body, null, 2) : '';
  // The bytes that arrived. A row captured before bodyRaw existed, or by an
  // entrypoint that only has the parsed value, still renders: the best
  // available raw text is then the serialised body.
  const rawBodyText =
    rawBody !== ''
      ? rawBody
      : typeof request.body === 'string'
        ? request.body
        : parsedBodyText;
  const hasBody = rawBodyText !== '';
  // The switch is only offered when there are two different things to see: a
  // body that parsed, and the bytes it parsed from.
  const canShowParsed = bodyIsObject && rawBody !== '';
  const bodyText = bodyView === 'parsed' && canShowParsed ? parsedBodyText : rawBodyText;
  const curlCommand = generateCurlCommand(request, slug, apiUrl);
  // The answer this endpoint gave, as recorded on the row. A row captured
  // before the answer was stored has none of it, and then the pane must render
  // exactly as it did before this section existed: no section, no badge, and no
  // `undefined` standing in for a value that was never there.
  const responseStatus = typeof request.status === 'number' ? request.status : null;
  const responseContentType =
    typeof request.responseContentType === 'string' && request.responseContentType !== ''
      ? request.responseContentType
      : null;
  const responseBodyBytes =
    typeof request.responseBodyBytes === 'number' && Number.isFinite(request.responseBodyBytes)
      ? request.responseBodyBytes
      : null;
  // The headers the answer carried, as the row recorded them. A row captured
  // before they were stored has none — and one written by anything else could
  // hold anything, so the entries are read as pairs of strings or not at all.
  const responseHeaderEntries =
    request.responseHeaders &&
    typeof request.responseHeaders === 'object' &&
    !Array.isArray(request.responseHeaders)
      ? (Object.entries(request.responseHeaders) as [unknown, unknown][]).filter(
          (entry): entry is [string, string] => typeof entry[1] === 'string',
        )
      : [];
  const responseText = [
    `Status: ${responseStatus}`,
    responseContentType ? `Content-Type: ${responseContentType}` : null,
    responseBodyBytes !== null ? `Body size: ${formatBytes(responseBodyBytes)}` : null,
    ...responseHeaderEntries.map(([name, value]) => `${name}: ${value}`),
  ]
    .filter(Boolean)
    .join('\n');
  const summaryText = [
    `Method: ${request.method}`,
    request.path ? `Path: ${request.path}` : null,
    `Received: ${formatDate(request.receivedAt)}`,
    request.ip ? `IP: ${request.ip}` : null,
    request.userAgent ? `User-Agent: ${request.userAgent}` : null,
  ]
    .filter(Boolean)
    .join('\n');

  return (
    <div className="detail-pane">
      <header className="detail-pane__head">
        <h3 className="detail-pane__title">
          <Badge bg={methodVariant(request.method)} className="method-badge">
            {request.method}
          </Badge>
          {request.path ? <code className="detail-pane__path">{request.path}</code> : null}
        </h3>
        <span className="detail-pane__time">{formatDate(request.receivedAt)}</span>
      </header>

      <Section
        title="Basic Info"
        icon={faInfoCircle}
        copyField="basic"
        copyText={summaryText}
        copyLabel="Copy request summary"
        copiedField={copiedField}
        onCopy={copyToClipboard}
      >
        <dl className="detail-list">
          <DefItem label="Method" field="method" text={request.method} copiedField={copiedField} onCopy={copyToClipboard}>
            <Badge bg={methodVariant(request.method)} className="method-badge">
              {request.method}
            </Badge>
          </DefItem>
          {request.path ? (
            <DefItem label="Path" field="path" text={request.path} copiedField={copiedField} onCopy={copyToClipboard}>
              <code>{request.path}</code>
            </DefItem>
          ) : null}
          <DefItem
            label="Received At"
            field="receivedAt"
            text={formatDate(request.receivedAt)}
            copiedField={copiedField}
            onCopy={copyToClipboard}
          >
            {formatDate(request.receivedAt)}
          </DefItem>
          {request.ip ? (
            <DefItem label="IP Address" field="ip" text={request.ip} copiedField={copiedField} onCopy={copyToClipboard}>
              <code>{request.ip}</code>
            </DefItem>
          ) : null}
          {request.userAgent ? (
            <DefItem
              label="User Agent"
              field="userAgent"
              text={request.userAgent}
              copiedField={copiedField}
              onCopy={copyToClipboard}
            >
              <span className="detail-list__wrap">{request.userAgent}</span>
            </DefItem>
          ) : null}
          <DefItem label="Retention">
            <Badge bg={retentionVariant(request.daysUntilDeletion)} className="retention-badge">
              {retentionLabel(request.daysUntilDeletion)}
            </Badge>
          </DefItem>
        </dl>
      </Section>

      {responseStatus !== null ? (
        <Section
          title="Response"
          icon={faReply}
          copyField="response"
          copyText={responseText}
          copyLabel="Copy response summary"
          copiedField={copiedField}
          onCopy={copyToClipboard}
        >
          <dl className="detail-list">
            <DefItem label="Status">
              <Badge bg={statusVariant(responseStatus)} className="status-badge">
                {responseStatus}
              </Badge>
            </DefItem>
            {responseContentType ? (
              <DefItem
                label="Content-Type"
                field="responseContentType"
                text={responseContentType}
                copiedField={copiedField}
                onCopy={copyToClipboard}
              >
                <code>{responseContentType}</code>
              </DefItem>
            ) : null}
            {responseBodyBytes !== null ? (
              <DefItem label="Body Size">{formatBytes(responseBodyBytes)}</DefItem>
            ) : null}
            {/* The headers the answer carried, each as its own row: what the
                sender received, record and wire agreeing. */}
            {responseHeaderEntries.map(([name, value]) => (
              <React.Fragment key={name}>
                <dt>
                  <span className="detail-list__wrap">{name}</span>
                </dt>
                <dd>
                  <code className="detail-list__value">{value}</code>
                  <CopyButton
                    field={`responseHeader:${name}`}
                    text={`${name}: ${value}`}
                    label={`Copy ${name}`}
                    copiedField={copiedField}
                    onCopy={copyToClipboard}
                  />
                </dd>
              </React.Fragment>
            ))}
          </dl>
        </Section>
      ) : null}

      {queryCount > 0 ? (
        <Section
          title={`Query Parameters (${queryCount})`}
          icon={faListUl}
          copyField="queryParams"
          copyText={queryText}
          copyLabel="Copy query parameters"
          copiedField={copiedField}
          onCopy={copyToClipboard}
        >
          <pre className="code-block">
            <code>{queryText}</code>
          </pre>
        </Section>
      ) : null}

      <Section
        title="Headers"
        icon={faCode}
        copyField="headers"
        copyText={headersText}
        copyLabel="Copy headers"
        copiedField={copiedField}
        onCopy={copyToClipboard}
      >
        <pre className="code-block">
          <code>{headersText}</code>
        </pre>
      </Section>

      {hasBody ? (
        <Section
          title="Request Body"
          icon={faCode}
          copyField="body"
          copyText={bodyText}
          copyLabel="Copy request body"
          copiedField={copiedField}
          onCopy={copyToClipboard}
        >
          {canShowParsed ? (
            <div className="body-view-toggle" role="group" aria-label="Request body view">
              <Button
                type="button"
                size="sm"
                variant={bodyView === 'raw' ? 'primary' : 'outline-secondary'}
                aria-pressed={bodyView === 'raw'}
                onClick={() => setBodyView('raw')}
              >
                Raw
              </Button>
              <Button
                type="button"
                size="sm"
                variant={bodyView === 'parsed' ? 'primary' : 'outline-secondary'}
                aria-pressed={bodyView === 'parsed'}
                onClick={() => setBodyView('parsed')}
              >
                Parsed
              </Button>
            </div>
          ) : null}
          <pre className="code-block" data-body-view={canShowParsed ? bodyView : 'raw'}>
            <code>{bodyText}</code>
          </pre>
        </Section>
      ) : null}

      <Section
        title="cURL Command"
        icon={faTerminal}
        copyField="curl"
        copyText={curlCommand}
        copyLabel="Copy cURL command"
        copiedField={copiedField}
        onCopy={copyToClipboard}
      >
        <pre className="code-block code-block--terminal" style={{ overflowX: 'auto' }}>
          <code>{curlCommand}</code>
        </pre>
        <p className="detail-hint">Copy this command to replay the same request in your terminal</p>
      </Section>

      <div className="detail-pane__actions">
        <Button variant="outline-secondary" size="sm" onClick={() => onExport(request)}>
          <FontAwesomeIcon icon={faDownload} aria-hidden="true" className="me-2" />
          Export as JSON
        </Button>
      </div>
    </div>
  );
};

export default RequestDetail;
